import {
  AccountBalance,
  Assessment,
  CheckCircle,
  Download,
  Print,
  TrendingUp,
} from "@mui/icons-material";
import {
  Alert,
  Backdrop,
  Box,
  Card,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SummaryCards, { formatNumber } from "./SummeryCard";
import AccountTable from "./AccountTable";
import MDBox from "components/MDBox";
import { useState } from "react";
import { useGetFinancialPeriodsQuery } from "../../../redux/Slices/financial-periodsApi";
const { useGetBalanceSheetQuery } = require("../../../redux/Slices/finanical-reportsApi");

// Main Balance Sheet Component
const BalanceSheetReport = () => {
  // get financial periods for filter dropdown
  const { data: periods, isLoading: periodsLoading } = useGetFinancialPeriodsQuery();
  const [period, setPeriod] = useState(4);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split("T")[0]);

  const filterQuery = new URLSearchParams({
    fiscal_period_id: period.toString(), // Ensure it's a string
    as_of_date: asOfDate,
  }).toString();

  // Fetch balance sheet data based on filters
  const { data, error, isLoading } = useGetBalanceSheetQuery(filterQuery);

  if (isLoading) {
    return (
     <Backdrop open={isLoading} >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f7fa"
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6">خطأ في تحميل البيانات</Typography>
          <Typography variant="body2">{error.message || "حدث خطأ أثناء جلب البيانات"}</Typography>
        </Alert>
      </Box>
    );
  }

  const { assets, liabilities, equity, summary } = data?.data || {};

  const { fiscal_period, as_of_date, generated_at } = data?.meta || {};

  return (
    <>
      <Backdrop open={isLoading} >
        <CircularProgress color="inherit" />
      </Backdrop>
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            borderRadius="lg"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              background: "linear-gradient(90deg, #ca9937, #e0b15a)",
              boxShadow: 3,
            }}
          >
            <Box>
              <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                <Assessment sx={{ fontSize: 40 }} />
                <Typography variant="h4" sx={{ color: "#111" }}>
                  الميزانية العمومية
                </Typography>
              </Stack>
              <Typography variant="body1" sx={{ color: "#eee" }}>
                الفترة المالية: {fiscal_period}
              </Typography>
              <Typography variant="body2" sx={{ color: "#eee" }}>
                كما في: {as_of_date} | تم الإنشاء: {generated_at}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Tooltip title="تحميل PDF">
                <IconButton sx={{ color: "text.main" }}>
                  <Download />
                </IconButton>
              </Tooltip>
              <Tooltip title="طباعة">
                <IconButton sx={{ color: "text.main" }}>
                  <Print />
                </IconButton>
              </Tooltip>
            </Stack>
          </MDBox>
          {/* Filters can be added here in the future */}
          <Box display="flex" alignItems="center" gap={2} sx={{ p: 2 }}>
            <FormControl flex={1} sx={{}}>
              <InputLabel id="type"> اختر الفترة </InputLabel>
              <Select
                label="اختر الفترة "
                /* error={!!errors.type} */
                name="type"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                sx={{ height: 43 }}
              >
                {periods?.data?.map((period) => (
                  <MenuItem key={period.id} value={period.id}>
                    {period.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl flex={1} sx={{}}>
              <TextField
                id="as-of-date"
                type="date"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
                sx={{}}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </FormControl>
          </Box>
          <Box  sx={{ minHeight: "100vh", py: 4  }} >
            <Container maxWidth="xl" sx={{display: "flex", flexDirection: "column", gap: 2}}>
              {/* Header */}

              {/* Summary Cards */}
              {summary && <SummaryCards summary={summary} />}

              {/* Assets Section */}
              {assets && (
                <AccountTable
                  title="الأصول"
                  accounts={assets.accounts}
                  total={assets.total}
                  icon={<AccountBalance />}
                  color="#1976d2"
                />
              )}

              {/* Liabilities Section */}
              {liabilities && (
                <AccountTable
                  title="الخصوم"
                  accounts={liabilities.accounts}
                  total={liabilities.total}
                  icon={<TrendingUp />}
                  color="#f57c00"
                />
              )}

              {/* Equity Section */}
              {equity && (
                <AccountTable
                  title=" حقوق الملكية"
                  accounts={equity.accounts}
                  total={equity.total_with_income}
                  icon={<CheckCircle />}
                  color="#2e7d32"
                />
               
              )}

              {/* Balance Verification */}
              {summary && (
                <Paper
                  elevation={4}
                  sx={{
                    p: 3,
                    background: summary.balance_check
                      ? "linear-gradient(135deg, #2e7d3215 0%, #2e7d3205 100%)"
                      : "linear-gradient(135deg, #d32f2f15 0%, #d32f2f05 100%)",
                    border: `2px solid ${summary.balance_check ? "#2e7d32" : "#d32f2f"}`,
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <CheckCircle
                      sx={{
                        fontSize: 40,
                        color: summary.balance_check ? "#2e7d32" : "#d32f2f",
                      }}
                    />
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {summary.balance_check ? "الميزانية متوازنة ✓" : "الميزانية غير متوازنة ✗"}
                      </Typography>
                      <Typography variant="body2" color="text.main">
                        الفرق: {formatNumber(summary.difference)}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}
            </Container>
          </Box>
        </Card>
      </MDBox>
    </>
  );
};

export default BalanceSheetReport;
